// ANA-R3-GEOM-02 — pure geometry core for the shared PolygonEditor primitive.
//
// Everything in this file is host-agnostic and side-effect-free: no React, no
// Konva, no DOM. The PolygonEditor's interaction hook (`usePolygonEditor`) and
// the Konva render layer both build on these helpers, and the unit suite drives
// them directly so the geometry contract is verified without booting a canvas.
//
// COORDINATE SPACE. A `Point` is normalised 0..1 in image space (matching how
// GEOM-01 exposes `path` on read, and how `bbox` is already consumed). The host
// supplies a `CoordinateTransform` (see ./CoordinateTransform.ts) to map between
// these normalised image points and on-screen pixels for hit-testing + render.
//
// SERIALISATION. The wire shape is a flat `[x0,y0,x1,y1,…]` array. Closed iff
// the first vertex equals the last (GeoJSON-ring convention, GEOM-00 D8) — there
// is no separate `closed` flag. `rect` and `polygon` serialise closed; the
// difference is only that a `rect`'s closed quad is axis-aligned. `polyline`
// serialises open.

/** A single vertex in normalised 0..1 image space. */
export interface Point {
  x: number;
  y: number;
}

/**
 * The three editor modes (GEOM-00 D9). All create *and* edit.
 *  - `rect`     — constrained resize; serialises a closed axis-aligned quad.
 *  - `polygon`  — closed free-form ring; filled, has area.
 *  - `polyline` — open free-form line; stroke only, no area.
 */
export type EditorMode = "rect" | "polygon" | "polyline";

/**
 * The controlled value of the editor. `points` are the *vertices* (NOT the flat
 * serialised array, and NOT carrying the closing duplicate) — closure is implied
 * by `mode` (`rect`/`polygon` = closed, `polyline` = open). Use
 * {@link serialisePoints} to get the wire `[x0,y0,…]` form with the GeoJSON
 * closing duplicate, and {@link parsePath} to go the other way.
 */
export interface PolygonValue {
  mode: EditorMode;
  /** Distinct vertices, no closing duplicate. */
  points: Point[];
}

/** Tolerance (in normalised units) for "is this the same point" / axis-aligned
 *  checks. ~1e-4 ≈ sub-pixel on an 8k image. */
export const EPSILON = 1e-4;

/** Minimum vertex counts per mode. */
export const MIN_POLYGON_VERTICES = 3;
export const MIN_POLYLINE_VERTICES = 2;

export function minVertices(mode: EditorMode): number {
  return mode === "polyline" ? MIN_POLYLINE_VERTICES : MIN_POLYGON_VERTICES;
}

export function isClosedMode(mode: EditorMode): boolean {
  return mode === "rect" || mode === "polygon";
}

function approxEqual(a: number, b: number, eps = EPSILON): boolean {
  return Math.abs(a - b) <= eps;
}

export function pointsEqual(a: Point, b: Point, eps = EPSILON): boolean {
  return approxEqual(a.x, b.x, eps) && approxEqual(a.y, b.y, eps);
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export function clampPoint(p: Point): Point {
  return { x: clamp01(p.x), y: clamp01(p.y) };
}

/**
 * Serialise a value to the flat wire array `[x0,y0,x1,y1,…]`. Closed modes
 * (`rect`/`polygon`) repeat the first vertex as the last (GeoJSON ring);
 * `polyline` stays open. The input `points` must NOT already carry the closing
 * duplicate — {@link PolygonValue} stores distinct vertices.
 */
export function serialisePoints(value: PolygonValue): number[] {
  const pts = value.points;
  const flat: number[] = [];
  for (const p of pts) {
    flat.push(p.x, p.y);
  }
  if (isClosedMode(value.mode) && pts.length >= 2) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    // Only append the closing duplicate if it isn't already present.
    if (first && last && !pointsEqual(first, last)) {
      flat.push(first.x, first.y);
    }
  }
  return flat;
}

/**
 * Parse a flat wire array `[x0,y0,…]` into distinct vertices. Drops a trailing
 * closing duplicate if present (so callers get the same `points` shape the
 * editor stores). Returns `{ points, ringClosed }` where `ringClosed` is true
 * when the input had first==last (i.e. it was a closed ring on the wire).
 */
export function parsePath(flat: readonly number[]): {
  points: Point[];
  ringClosed: boolean;
} {
  const points: Point[] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const x = flat[i];
    const y = flat[i + 1];
    if (typeof x === "number" && typeof y === "number") {
      points.push({ x, y });
    }
  }
  let ringClosed = false;
  if (points.length >= 2) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first && last && pointsEqual(first, last)) {
      ringClosed = true;
      points.pop();
    }
  }
  return { points, ringClosed };
}

/**
 * Is the (distinct-vertex) ring an axis-aligned rectangle? True only for exactly
 * four vertices forming a closed quad whose edges are all horizontal/vertical
 * and that has non-zero area (degenerate lines are not rects).
 */
export function isAxisAlignedRect(points: readonly Point[]): boolean {
  if (points.length !== 4) return false;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const uniqX = uniqueWithin(xs);
  const uniqY = uniqueWithin(ys);
  // An axis-aligned quad uses exactly two distinct x's and two distinct y's.
  if (uniqX.length !== 2 || uniqY.length !== 2) return false;
  const [x0, x1] = uniqX;
  const [y0, y1] = uniqY;
  if (
    x0 === undefined ||
    x1 === undefined ||
    y0 === undefined ||
    y1 === undefined
  ) {
    return false;
  }
  // Non-degenerate: the two x's (and y's) must actually differ.
  if (approxEqual(x0, x1)) return false;
  if (approxEqual(y0, y1)) return false;
  // Every vertex must sit on one of the two x-lines and one of the two y-lines,
  // and consecutive edges must alternate horizontal/vertical.
  for (let i = 0; i < 4; i++) {
    const a = points[i];
    const b = points[(i + 1) % 4];
    if (!a || !b) return false;
    const horizontal = approxEqual(a.y, b.y);
    const vertical = approxEqual(a.x, b.x);
    if (horizontal === vertical) return false; // neither or both ⇒ not a rect
  }
  return true;
}

function uniqueWithin(values: number[], eps = EPSILON): number[] {
  const out: number[] = [];
  for (const v of values) {
    if (!out.some((u) => approxEqual(u, v, eps))) out.push(v);
  }
  return out;
}

/**
 * Pick the initial editor mode from a stored path (GEOM-00 D9, AIARCH-02 D5 —
 * findings/defects always carry a path, so there is no null-means-box signal).
 *
 *  - open ring (first != last)                ⇒ `polyline`
 *  - closed axis-aligned quad                 ⇒ `rect`
 *  - any other closed ring                    ⇒ `polygon`
 *
 * Accepts either a flat `[x0,y0,…]` array or a `Point[]`. For a `Point[]` the
 * ring is treated as closed iff first==last (or it is a 4-point axis-aligned
 * quad that is already a rect even without the explicit duplicate — a convenience
 * for callers that pass distinct corners).
 */
export function detectMode(
  path: readonly number[] | readonly Point[],
): EditorMode {
  const flat = isPointArray(path) ? pointArrayToFlat(path) : path;
  const { points, ringClosed } = parsePath(flat);
  if (points.length < 2) return "polyline";
  if (!ringClosed) {
    // An explicitly-open path is a polyline UNLESS the caller handed distinct
    // corners of an axis-aligned quad (4 points, no duplicate) — treat that as a
    // rect for convenience, matching "stored rect corners" callers.
    if (isAxisAlignedRect(points)) return "rect";
    return "polyline";
  }
  if (isAxisAlignedRect(points)) return "rect";
  return "polygon";
}

function isPointArray(
  path: readonly number[] | readonly Point[],
): path is readonly Point[] {
  return path.length > 0 && typeof path[0] === "object";
}

function pointArrayToFlat(points: readonly Point[]): number[] {
  const flat: number[] = [];
  for (const p of points) flat.push(p.x, p.y);
  return flat;
}

/** Shoelace area of a closed ring (normalised² units). Open paths ⇒ 0. */
export function polygonArea(points: readonly Point[]): number {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (!a || !b) continue;
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

// ---------------------------------------------------------------------------
// Self-intersection guard (ANA-R4-VALID-01).
//
// We forbid SELF-INTERSECTING (non-simple) geometry — crossing line markings and
// polygons whose edges cross. We do NOT forbid concavity: a concave-but-simple
// polygon (an L-shape, a crack outline) is perfectly valid. Downstream geometry
// libraries refuse the math for self-intersecting polygons, so we block at the
// finish step.
// ---------------------------------------------------------------------------

/** Orientation sign of the ordered triplet (a, b, c): >0 ccw, <0 cw, 0 collinear. */
function cross(o: Point, a: Point, b: Point): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

function onSegment(a: Point, b: Point, p: Point): boolean {
  return (
    Math.min(a.x, b.x) - EPSILON <= p.x &&
    p.x <= Math.max(a.x, b.x) + EPSILON &&
    Math.min(a.y, b.y) - EPSILON <= p.y &&
    p.y <= Math.max(a.y, b.y) + EPSILON
  );
}

/** Do the open segments a-b and c-d properly cross (interior intersection)? */
export function segmentsIntersect(
  a: Point,
  b: Point,
  c: Point,
  d: Point,
): boolean {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  if (
    ((d1 > EPSILON && d2 < -EPSILON) || (d1 < -EPSILON && d2 > EPSILON)) &&
    ((d3 > EPSILON && d4 < -EPSILON) || (d3 < -EPSILON && d4 > EPSILON))
  ) {
    return true;
  }
  // Collinear-overlap cases (a shared point that lies along the other segment).
  if (Math.abs(d1) <= EPSILON && onSegment(c, d, a)) return true;
  if (Math.abs(d2) <= EPSILON && onSegment(c, d, b)) return true;
  if (Math.abs(d3) <= EPSILON && onSegment(a, b, c)) return true;
  if (Math.abs(d4) <= EPSILON && onSegment(a, b, d)) return true;
  return false;
}

/**
 * Does the vertex list self-intersect? `closed` adds the closing edge (last→first).
 * Adjacent edges (which share a vertex) are skipped; only non-adjacent edge pairs
 * are tested, so a normal corner is not a "crossing".
 */
export function isSelfIntersecting(
  points: readonly Point[],
  closed: boolean,
): boolean {
  const n = points.length;
  if (n < 4) return false; // need ≥4 vertices for non-adjacent edges to exist
  const edgeCount = closed ? n : n - 1;
  for (let i = 0; i < edgeCount; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    if (!a || !b) continue;
    for (let j = i + 1; j < edgeCount; j++) {
      // skip adjacent edges (share a vertex) and, for closed rings, the
      // first/last wrap-around pair.
      if (j === i) continue;
      if (j === i + 1) continue;
      if (closed && i === 0 && j === edgeCount - 1) continue;
      const c = points[j];
      const d = points[(j + 1) % n];
      if (!c || !d) continue;
      if (segmentsIntersect(a, b, c, d)) return true;
    }
  }
  return false;
}

/** Axis-aligned bounding box of a vertex list (normalised). */
export function boundingBox(points: readonly Point[]): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// ---------------------------------------------------------------------------
// Mode conversions (GEOM-02 AC4).
// ---------------------------------------------------------------------------

/**
 * Convert a value to `polygon`:
 *  - from `rect`     → unlock the 4 corners as independent vertices (identical
 *    geometry; the corners are already the polygon's vertices).
 *  - from `polyline` → close the open line (append nothing; closure is implied
 *    by the mode). Requires ≥3 vertices.
 *  - from `polygon`  → no-op.
 */
export function toPolygon(value: PolygonValue): PolygonValue {
  return { mode: "polygon", points: value.points.map((p) => ({ ...p })) };
}

/**
 * Convert a value to `polyline` (open). Drops closure; geometry (vertices) is
 * unchanged. A `rect` becomes an open 4-vertex line; a `polygon` opens up.
 */
export function toPolyline(value: PolygonValue): PolygonValue {
  return { mode: "polyline", points: value.points.map((p) => ({ ...p })) };
}

/**
 * Convert a value to `rect`. If the vertices already form an axis-aligned quad
 * the geometry is preserved exactly (lossless round-trip); otherwise the rect is
 * the AABB of the vertices (lossy — the caller is expected to confirm, per
 * GEOM-00 UX model). The result is always the 4 corners TL→TR→BR→BL.
 */
export function toRect(value: PolygonValue): PolygonValue {
  const pts = value.points;
  if (isAxisAlignedRect(pts)) {
    return { mode: "rect", points: orderRectCorners(pts) };
  }
  const bb = boundingBox(pts);
  return { mode: "rect", points: rectCorners(bb) };
}

/** The 4 corners of a bbox in TL→TR→BR→BL order. */
export function rectCorners(bb: {
  x: number;
  y: number;
  width: number;
  height: number;
}): Point[] {
  return [
    { x: bb.x, y: bb.y },
    { x: bb.x + bb.width, y: bb.y },
    { x: bb.x + bb.width, y: bb.y + bb.height },
    { x: bb.x, y: bb.y + bb.height },
  ];
}

/** Normalise any axis-aligned quad's 4 corners to TL→TR→BR→BL order. */
function orderRectCorners(points: readonly Point[]): Point[] {
  return rectCorners(boundingBox(points));
}

/**
 * Constrained rect resize (GEOM-02 AC4): drag the corner at `cornerIndex`
 * (0=TL,1=TR,2=BR,3=BL ordering) to `to`; the diagonally-opposite corner stays
 * anchored and the rect stays axis-aligned. Returns the new TL→TR→BR→BL corners.
 */
export function resizeRect(
  corners: readonly Point[],
  cornerIndex: number,
  to: Point,
): Point[] {
  const bb = boundingBox(corners);
  // The opposite corner is the anchor.
  const corners4 = rectCorners(bb);
  const opposite = corners4[(cornerIndex + 2) % 4] ??
    corners4[0] ?? { x: bb.x, y: bb.y };
  const x0 = Math.min(opposite.x, to.x);
  const y0 = Math.min(opposite.y, to.y);
  const x1 = Math.max(opposite.x, to.x);
  const y1 = Math.max(opposite.y, to.y);
  return rectCorners({ x: x0, y: y0, width: x1 - x0, height: y1 - y0 });
}

// ---------------------------------------------------------------------------
// Vertex editing (GEOM-02 AC2).
// ---------------------------------------------------------------------------

/** Move vertex `index` to `to` (clamped to 0..1). */
export function moveVertex(
  points: readonly Point[],
  index: number,
  to: Point,
): Point[] {
  const next = points.map((p) => ({ ...p }));
  if (index >= 0 && index < next.length) next[index] = clampPoint(to);
  return next;
}

/** Insert a new vertex at array position `index` (between index-1 and index). */
export function insertVertex(
  points: readonly Point[],
  index: number,
  point: Point,
): Point[] {
  const next = points.map((p) => ({ ...p }));
  const clamped = Math.max(0, Math.min(points.length, index));
  next.splice(clamped, 0, clampPoint(point));
  return next;
}

/**
 * Remove vertex `index`, enforcing the per-mode minimum. Returns the unchanged
 * list (a no-op) when removal would drop below the minimum — the caller surfaces
 * feedback (GEOM-02 AC2: "Delete on a triangle is a no-op with feedback").
 */
export function removeVertex(
  points: readonly Point[],
  index: number,
  mode: EditorMode,
): { points: Point[]; removed: boolean } {
  if (points.length <= minVertices(mode)) {
    return { points: points.map((p) => ({ ...p })), removed: false };
  }
  if (index < 0 || index >= points.length) {
    return { points: points.map((p) => ({ ...p })), removed: false };
  }
  const next = points.map((p) => ({ ...p }));
  next.splice(index, 1);
  return { points: next, removed: true };
}

/**
 * The index of the edge nearest to `point`, plus the squared distance, for
 * "click an edge to insert a vertex". Edge `i` runs from vertex `i` to vertex
 * `i+1`; for closed modes the wrap-around edge (last→first) is included. The new
 * vertex would be inserted at position `i+1`.
 */
export function nearestEdge(
  points: readonly Point[],
  point: Point,
  mode: EditorMode,
): { edgeIndex: number; insertAt: number; distanceSq: number } | null {
  if (points.length < 2) return null;
  const closed = isClosedMode(mode);
  const edgeCount = closed ? points.length : points.length - 1;
  let best: { edgeIndex: number; insertAt: number; distanceSq: number } | null =
    null;
  for (let i = 0; i < edgeCount; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (!a || !b) continue;
    const d = distanceToSegmentSq(point, a, b);
    if (best === null || d < best.distanceSq) {
      best = { edgeIndex: i, insertAt: i + 1, distanceSq: d };
    }
  }
  return best;
}

function distanceToSegmentSq(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = p.x - a.x;
    const ey = p.y - a.y;
    return ex * ex + ey * ey;
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  const ex = p.x - cx;
  const ey = p.y - cy;
  return ex * ex + ey * ey;
}

/** Squared distance between two points. */
export function distanceSq(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
